//frontend/src/components/AdminRequests/AdminRequestDetail.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminRequestChat from "./Chat";

import {
  createComment,
  createEvent,
  deleteComment,
  deleteDocument,
  deleteEvent,
  deleteRequestSelection,
  getComments,
  getCurrentUser,
  getDocuments,
  getEvents,
  getRequestSelection,
  updateComment,
  updateDocument,
  listUsers,
  updateEvent,
  updateRequestSelection,
  uploadDocument,
} from "../api/requestSelection/requestSelectionApi";

import {
  EDITABLE_STATUS_OPTIONS,
  Modal,
  StatusBadge,
  fmtDate,
  fmtDateOnly,
  isAdmin,
  toLocalInput,
} from "./constants";
import "./AdminRequests.css";

export default function AdminRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const requestId = id;
  const onBack = () => navigate("/request-search");

  const [request, setRequest] = useState(null);
  const [comments, setComments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canManage, setCanManage] = useState(false);

  // ─── Общая модалка редактирования ─────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    status: "sent",
    search_manager_id: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  const [commentOpen, setCommentOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);

  const [docOpen, setDocOpen] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docName, setDocName] = useState("");
  const [docComment, setDocComment] = useState("");
  const [uploading, setUploading] = useState(false);
  const [docEditOpen, setDocEditOpen] = useState(false);
  const [docEditId, setDocEditId] = useState(null);
  const [docEditName, setDocEditName] = useState("");
  const [docEditComment, setDocEditComment] = useState("");

  const [eventOpen, setEventOpen] = useState(false);
  const [eventDraft, setEventDraft] = useState({
    title: "",
    start_time: "",
    end_time: "",
    color: "blue",
  });
  const [editingEventId, setEditingEventId] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [req, cmts, docs, evs, user, allUsers] = await Promise.all([
        getRequestSelection(requestId),
        getComments(requestId),
        getDocuments(requestId),
        getEvents(requestId),
        getCurrentUser(),
        listUsers(),
      ]);
      setRequest(req);
      setComments(cmts);
      setDocuments(docs);
      setEvents(evs);
      setMe(user);
      setCanManage(isAdmin(user));
      setUsers(allUsers);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (requestId) loadAll();
  }, [requestId]);

  // ─── Список менеджеров по поиску (procurement) ────────────
  const searchManagers = useMemo(() => {
    const proc = users.filter((u) =>
      (u.groups || []).some((g) => g.name === "procurement")
    );
    const currentId = request?.search_manager_id;
    if (currentId && !proc.find((u) => u.id === currentId)) {
      const current = users.find((u) => u.id === currentId);
      if (current) return [current, ...proc];
    }
    return proc;
  }, [users, request]);

  const openEdit = () => {
    setEditForm({
      title: request?.title || "",
      status: request?.status || "sent",
      search_manager_id: request?.search_manager_id || "",
      description: request?.description || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        title: editForm.title.trim(),
        status: editForm.status,
        description: editForm.description,
        search_manager_id: editForm.search_manager_id
          ? Number(editForm.search_manager_id)
          : null,
      };
      const updated = await updateRequestSelection(requestId, payload);
      setRequest(updated);
      setEditOpen(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteRequest = async () => {
    if (!window.confirm("Удалить заявку?")) return;
    try {
      await deleteRequestSelection(requestId);
      onBack();
    } catch (e) {
      alert(e.message);
    }
  };

  const openNewComment = () => {
    setCommentDraft("");
    setEditingCommentId(null);
    setCommentOpen(true);
  };
  const openEditComment = (c) => {
    setEditingCommentId(c.id);
    setCommentDraft(c.text);
    setCommentOpen(true);
  };
  const saveComment = async () => {
    if (!commentDraft.trim()) return;
    try {
      if (editingCommentId) {
        const updated = await updateComment(editingCommentId, commentDraft);
        setComments((prev) =>
          prev.map((c) => (c.id === editingCommentId ? updated : c))
        );
      } else {
        const created = await createComment(requestId, commentDraft);
        setComments((prev) => [...prev, created]);
      }
      setCommentOpen(false);
    } catch (e) {
      alert(e.message);
    }
  };
  const removeComment = async (id) => {
    if (!window.confirm("Удалить комментарий?")) return;
    try {
      await deleteComment(id);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      alert(e.message);
    }
  };

  const openNewDoc = () => {
    setDocFile(null);
    setDocName("");
    setDocComment("");
    setDocOpen(true);
  };
  const saveDoc = async (e) => {
    e.preventDefault();
    if (!docFile) return;
    setUploading(true);
    try {
      const created = await uploadDocument({
        request: requestId,
        file: docFile,
        name: docName || docFile.name,
        comment: docComment,
      });
      setDocuments((prev) => [...prev, created]);
      setDocOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  const editDoc = (doc) => {
    setDocEditId(doc.id);
    setDocEditName(doc.name || "");
    setDocEditComment(doc.comment || "");
    setDocEditOpen(true);
  };

  const saveDocEdit = async () => {
    if (!docEditId) return;
    try {
      const updated = await updateDocument(docEditId, {
        name: docEditName,
        comment: docEditComment,
      });
      setDocuments((prev) =>
        prev.map((d) => (d.id === docEditId ? updated : d))
      );
      setDocEditOpen(false);
    } catch (e) {
      alert(e.message);
    }
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Удалить документ?")) return;
    try {
      await deleteDocument(id);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      alert(e.message);
    }
  };

  const openNewEvent = () => {
    setEditingEventId(null);
    setEventDraft({ title: "", start_time: "", end_time: "", color: "blue" });
    setEventOpen(true);
  };
  const openEditEvent = (ev) => {
    setEditingEventId(ev.id);
    setEventDraft({
      title: ev.title || "",
      start_time: toLocalInput(ev.start_time || ev.start_at),
      end_time: toLocalInput(ev.end_time || ev.end_at),
      color: ev.color || "blue",
    });
    setEventOpen(true);
  };
  const saveEvent = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: eventDraft.title,
        start_time: new Date(eventDraft.start_time).toISOString(),
        end_time: new Date(
          eventDraft.end_time || eventDraft.start_time
        ).toISOString(),
        color: eventDraft.color,
      };
      if (editingEventId) {
        const updated = await updateEvent(editingEventId, payload);
        setEvents((prev) =>
          prev.map((ev) => (ev.id === editingEventId ? updated : ev))
        );
      } else {
        const created = await createEvent({
          ...payload,
          request_selection: Number(requestId),
        });
        setEvents((prev) => [...prev, created]);
      }
      setEventOpen(false);
    } catch (err) {
      alert(err.message);
    }
  };
  const removeEvent = async (id) => {
    if (!window.confirm("Удалить событие?")) return;
    try {
      await deleteEvent(id);
      setEvents((prev) => prev.filter((ev) => ev.id !== id));
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading) {
    return (
      <div className="proc-page">
        <div className="proc-empty proc-empty--large">
          <h3>Загрузка заявки...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="proc-page">
        <button className="proc-back-button" onClick={onBack}>
          ← Все заявки
        </button>
        <div className="proc-empty proc-empty--large">
          <div className="proc-empty__icon">⚠️</div>
          <h3>{error}</h3>
          <button className="proc-button proc-button--secondary" onClick={loadAll}>
            Повторить
          </button>
        </div>
      </div>
    );
  }

  if (!request) return null;

  return (
    <div className="proc-page">
      <div className="proc-page__header">
        <div>
          <button className="proc-back-button" onClick={onBack}>
            ← Все заявки
          </button>
          <div className="proc-request-heading">
            <div>
              <div className="proc-request-id">{request.number}</div>
              <h1>{request.title || `Заявка ${request.number}`}</h1>
            </div>
            <StatusBadge status={request.status} />
          </div>
        </div>
        {canManage && (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="proc-button proc-button--secondary"
              onClick={openEdit}
            >
              ✎ Редактировать
            </button>
            <button
              className="proc-button"
              style={{ background: "#c23d67" }}
              onClick={deleteRequest}
            >
              Удалить заявку
            </button>
          </div>
        )}
      </div>

      <main>
        {/* ==== Информация о заявке ==== */}
        <section className="proc-section">
          <div className="proc-section__title">
            <div>
              <h2>Информация о заявке</h2>
              <span>Основные данные</span>
            </div>
          </div>
          <div className="proc-description">
            <p><strong>Номер:</strong> {request.number}</p>
            <p>
              <strong>Товар:</strong>{" "}
              {request.title || <span className="proc-muted">не задан</span>}
            </p>
            <p>
              <strong>Клиент:</strong>{" "}
              {request.client_name || (
                <span className="proc-muted">не указан</span>
              )}
            </p>
            <p>
              <strong>Администратор:</strong>{" "}
              {request.client_manager_name || `#${request.client_manager_id}`}
            </p>
            <p>
              <strong>Менеджер по поиску:</strong>{" "}
              {request.search_manager_name ||
                (request.search_manager_id
                  ? `#${request.search_manager_id}`
                  : "не назначен")}
            </p>
            <p><strong>Создана:</strong> {fmtDate(request.created_at)}</p>
            <p><strong>Обновлена:</strong> {fmtDate(request.updated_at)}</p>
            {request.matrix_room_id && (
              <p>
                <strong>Matrix Room ID:</strong>{" "}
                <code>{request.matrix_room_id}</code>
              </p>
            )}
          </div>
        </section>

        {/* ==== Описание ==== */}
        <section className="proc-section">
          <div className="proc-section__title">
            <div>
              <h2>Требования клиента</h2>
              <span>Описание задачи</span>
            </div>
          </div>
          <div className="proc-description">
            {request.description || "Описание не указано."}
          </div>
        </section>

        {/* ==== Документы ==== */}
        <section className="proc-section">
          <div className="proc-section__title">
            <div>
              <h2>Документы</h2>
              <span>{documents.length} файлов</span>
            </div>
            {canManage && (
              <button className="proc-button" onClick={openNewDoc}>
                ＋ Добавить документ
              </button>
            )}
          </div>
          <div className="proc-description">
            {documents.length === 0 && (
              <div className="proc-empty">Документов пока нет</div>
            )}
            {documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  padding: 10,
                  marginBottom: 8,
                  background: "#f9fafb",
                  borderRadius: 8,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    📎 {doc.name || "файл"}
                  </div>
                  {doc.comment && (
                    <div
                      style={{ color: "#6b7280", marginTop: 4, fontSize: 13 }}
                    >
                      {doc.comment}
                    </div>
                  )}
                  <div
                    style={{ color: "#9ca3af", marginTop: 4, fontSize: 11 }}
                  >
                    {fmtDate(doc.created_at)}
                  </div>
                </div>
                <a
                  href={
                    doc.url && doc.url.startsWith("http")
                      ? doc.url
                      : `${process.env.REACT_APP_DJANGO_BACKEND}${doc.url}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="proc-button proc-button--secondary"
                  style={{ textDecoration: "none", alignSelf: "center" }}
                >
                  ↓ Скачать
                </a>
                {canManage && (
                  <>
                    <button
                      className="proc-icon-button"
                      onClick={() => editDoc(doc)}
                      title="Редактировать"
                    >
                      ✎
                    </button>
                    <button
                      className="proc-icon-button"
                      style={{ color: "#c23d67" }}
                      onClick={() => removeDoc(doc.id)}
                      title="Удалить"
                    >
                      ×
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ==== Комментарии ==== */}
        <section className="proc-section">
          <div className="proc-section__title">
            <div>
              <h2>Комментарии</h2>
              <span>{comments.length}</span>
            </div>
            <button className="proc-button" onClick={openNewComment}>
              ＋ Добавить комментарий
            </button>
          </div>
          <div className="proc-description">
            {comments.length === 0 && (
              <div className="proc-empty">Комментариев нет</div>
            )}
            {comments.map((c) => (
              <div
                key={c.id}
                style={{
                  padding: 10,
                  marginBottom: 8,
                  background: "#f9fafb",
                  borderRadius: 8,
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <strong>{c.author_name}</strong>
                  <span style={{ color: "#9ca3af", fontSize: 11 }}>
                    {fmtDate(c.created_at)}
                  </span>
                </div>
                <div style={{ marginTop: 6 }}>{c.text}</div>
                {canManage && (
                  <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                    <button
                      className="proc-icon-button"
                      onClick={() => openEditComment(c)}
                    >
                      ✎
                    </button>
                    <button
                      className="proc-icon-button"
                      style={{ color: "#c23d67" }}
                      onClick={() => removeComment(c.id)}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ==== События ==== */}
        <section className="proc-section">
          <div className="proc-section__title">
            <div>
              <h2>События</h2>
              <span>{events.length}</span>
            </div>
            {canManage && (
              <button className="proc-button" onClick={openNewEvent}>
                ＋ Добавить событие
              </button>
            )}
          </div>
          <div className="proc-description">
            {events.length === 0 && (
              <div className="proc-empty">Событий нет</div>
            )}
            {events.map((ev) => (
              <div
                key={ev.id}
                style={{
                  padding: 10,
                  marginBottom: 8,
                  background: "#f9fafb",
                  borderRadius: 8,
                  borderLeft: `4px solid ${ev.color || "#3B82F6"}`,
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{ev.title}</div>
                  <div
                    style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}
                  >
                    {fmtDateOnly(ev.start_time || ev.start_at)} —{" "}
                    {fmtDateOnly(ev.end_time || ev.end_at)}
                  </div>
                </div>
                {canManage && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="proc-icon-button"
                      onClick={() => openEditEvent(ev)}
                    >
                      ✎
                    </button>
                    <button
                      className="proc-icon-button"
                      style={{ color: "#c23d67" }}
                      onClick={() => removeEvent(ev.id)}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* ========================================================= */}
      {/* ПЛАВАЮЩИЙ ЧАТ С MATRIX                                    */}
      {/* ========================================================= */}
      {me && request?.matrix_room_id && (
        <AdminRequestChat
          matrixRoomId={request.matrix_room_id}
          matrixUserId={me.matrix?.user_id}
          accessToken={me.matrix?.access_token}
        />
      )}

      {/* ==== Модалка: Редактировать заявку ==== */}
      {editOpen && (
        <Modal
          title="Редактировать заявку"
          onClose={() => setEditOpen(false)}
          width={600}
        >
          <div className="proc-modal__form">
            <label className="proc-modal__label">
              Название (товар)
              <input
                className="proc-input"
                type="text"
                value={editForm.title}
                maxLength={100}
                onChange={(e) =>
                  setEditForm({ ...editForm, title: e.target.value })
                }
              />
            </label>

            <label className="proc-modal__label">
              Статус
              <select
                className="proc-input"
                value={editForm.status}
                onChange={(e) =>
                  setEditForm({ ...editForm, status: e.target.value })
                }
              >
                {EDITABLE_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="proc-modal__label">
              Менеджер по поиску
              <select
                className="proc-input"
                value={editForm.search_manager_id}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    search_manager_id: e.target.value,
                  })
                }
              >
                <option value="">— не назначен —</option>
                {searchManagers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name || u.username}
                  </option>
                ))}
              </select>
            </label>

            <label className="proc-modal__label">
              Описание
              <textarea
                className="proc-input proc-textarea"
                rows={6}
                value={editForm.description}
                onChange={(e) =>
                  setEditForm({ ...editForm, description: e.target.value })
                }
              />
            </label>

            <div className="proc-modal__actions">
              <button
                type="button"
                className="proc-button proc-button--secondary"
                onClick={() => setEditOpen(false)}
                disabled={saving}
              >
                Отмена
              </button>
              <button
                type="button"
                className="proc-button"
                onClick={saveEdit}
                disabled={saving}
              >
                {saving ? "Сохранение..." : "Сохранить"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ==== Модалки ==== */}
      {commentOpen && (
        <Modal
          title={
            editingCommentId
              ? "Редактировать комментарий"
              : "Новый комментарий"
          }
          onClose={() => setCommentOpen(false)}
        >
          <textarea
            className="proc-input proc-textarea"
            rows={5}
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            style={{ width: "100%" }}
          />
          <div className="proc-modal__actions">
            <button
              className="proc-button proc-button--secondary"
              onClick={() => setCommentOpen(false)}
            >
              Отмена
            </button>
            <button className="proc-button" onClick={saveComment}>
              {editingCommentId ? "Сохранить" : "Добавить"}
            </button>
          </div>
        </Modal>
      )}

      {docOpen && (
        <Modal
          title="Добавить документ"
          onClose={() => setDocOpen(false)}
          width={520}
        >
          <form onSubmit={saveDoc} className="proc-modal__form">
            <label className="proc-modal__label">
              Файл *
              <input
                type="file"
                required
                onChange={(e) => setDocFile(e.target.files?.[0] || null)}
              />
            </label>
            <label className="proc-modal__label">
              Название
              <input
                className="proc-input"
                type="text"
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                placeholder={docFile?.name || "Название файла"}
              />
            </label>
            <label className="proc-modal__label">
              Комментарий
              <textarea
                className="proc-input proc-textarea"
                rows={3}
                value={docComment}
                onChange={(e) => setDocComment(e.target.value)}
              />
            </label>
            <div className="proc-modal__actions">
              <button
                type="button"
                className="proc-button proc-button--secondary"
                onClick={() => setDocOpen(false)}
                disabled={uploading}
              >
                Отмена
              </button>
              <button
                type="submit"
                className="proc-button"
                disabled={!docFile || uploading}
              >
                {uploading ? "Загрузка..." : "Загрузить"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {eventOpen && (
        <Modal
          title={
            editingEventId ? "Редактировать событие" : "Новое событие"
          }
          onClose={() => setEventOpen(false)}
          width={520}
        >
          <form onSubmit={saveEvent} className="proc-modal__form">
            <label className="proc-modal__label">
              Название *
              <input
                className="proc-input"
                type="text"
                value={eventDraft.title}
                onChange={(e) =>
                  setEventDraft({ ...eventDraft, title: e.target.value })
                }
                required
              />
            </label>
            <label className="proc-modal__label">
              Начало *
              <input
                className="proc-input"
                type="datetime-local"
                value={eventDraft.start_time}
                onChange={(e) =>
                  setEventDraft({ ...eventDraft, start_time: e.target.value })
                }
                required
              />
            </label>
            <label className="proc-modal__label">
              Окончание
              <input
                className="proc-input"
                type="datetime-local"
                value={eventDraft.end_time}
                onChange={(e) =>
                  setEventDraft({ ...eventDraft, end_time: e.target.value })
                }
              />
            </label>
            <label className="proc-modal__label">
              Цвет
              <select
                className="proc-input"
                value={eventDraft.color}
                onChange={(e) =>
                  setEventDraft({ ...eventDraft, color: e.target.value })
                }
              >
                <option value="blue">Синий</option>
                <option value="red">Красный</option>
                <option value="green">Зелёный</option>
                <option value="yellow">Жёлтый</option>
                <option value="orange">Оранжевый</option>
                <option value="purple">Фиолетовый</option>
                <option value="pink">Розовый</option>
                <option value="gray">Серый</option>
                <option value="black">Чёрный</option>
              </select>
            </label>
            <div className="proc-modal__actions">
              <button
                type="button"
                className="proc-button proc-button--secondary"
                onClick={() => setEventOpen(false)}
              >
                Отмена
              </button>
              <button type="submit" className="proc-button">
                {editingEventId ? "Сохранить" : "Добавить"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {docEditOpen && (
        <Modal
          title="Редактировать документ"
          onClose={() => setDocEditOpen(false)}
          width={520}
        >
          <div className="proc-modal__form">
            <label className="proc-modal__label">
              Название
              <input
                className="proc-input"
                type="text"
                value={docEditName}
                onChange={(e) => setDocEditName(e.target.value)}
              />
            </label>
            <label className="proc-modal__label">
              Комментарий
              <textarea
                className="proc-input proc-textarea"
                rows={4}
                value={docEditComment}
                onChange={(e) => setDocEditComment(e.target.value)}
                autoFocus
              />
            </label>
            <div className="proc-modal__actions">
              <button
                type="button"
                className="proc-button proc-button--secondary"
                onClick={() => setDocEditOpen(false)}
              >
                Отмена
              </button>
              <button className="proc-button" onClick={saveDocEdit}>
                Сохранить
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}